import {
  StrictMode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createRoot } from "react-dom/client";
import {
  company,
  copy,
  languageFromPath,
  pageFromPath,
  pathFor,
  teamEn,
  teamTh,
  type Language,
  type Page,
} from "./content";
import "./styles.css";

if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

type PageProps = {
  language: Language;
};

const ArrowIcon = ({ direction = "right" }: { direction?: "right" | "down" }) => (
  <svg
    aria-hidden="true"
    className={`arrow-icon arrow-icon--${direction}`}
    viewBox="0 0 24 24"
  >
    <path d="M5 12h13M13 6l6 6-6 6" />
  </svg>
);

const MenuIcon = ({ open }: { open: boolean }) => (
  <span className={`menu-icon ${open ? "menu-icon--open" : ""}`} aria-hidden="true">
    <span />
    <span />
  </span>
);

function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "reveal--visible" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </div>
  );
}

function ScrollProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const update = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? window.scrollY / scrollable : 0);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return <span className="scroll-progress" style={{ transform: `scaleX(${progress})` }} />;
}

function Header({ language, page }: { language: Language; page: Page }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const text = copy[language];
  const navItems: Array<[Page, string]> = [
    ["home", text.nav.home],
    ["team", text.nav.team],
    ["careers", text.nav.careers],
    ["contact", text.nav.contact],
  ];

  return (
    <>
      <header className="site-header">
        <a
          className="brand"
          href={pathFor(language, "home")}
          aria-label="Power Asset Management"
        >
          <img src="/assets/logo-color.jpg" alt="Power Asset Management" />
        </a>

        <nav className={`main-nav ${menuOpen ? "main-nav--open" : ""}`} aria-label="Main">
          {navItems.map(([itemPage, label]) => (
            <a
              className={page === itemPage ? "is-active" : ""}
              href={pathFor(language, itemPage)}
              key={itemPage}
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="header-actions">
          <div className="language-switcher" aria-label="Language">
            <a
              className={language === "th" ? "is-active" : ""}
              href={pathFor("th", page)}
              lang="th"
            >
              TH
            </a>
            <span aria-hidden="true">/</span>
            <a
              className={language === "en" ? "is-active" : ""}
              href={pathFor("en", page)}
              lang="en"
            >
              EN
            </a>
          </div>
          <button
            className="menu-button"
            type="button"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMenuOpen((current) => !current)}
          >
            <MenuIcon open={menuOpen} />
          </button>
        </div>
      </header>
      <ScrollProgress />
    </>
  );
}

function Eyebrow({ children, light = false }: { children: ReactNode; light?: boolean }) {
  return <p className={`eyebrow ${light ? "eyebrow--light" : ""}`}>{children}</p>;
}

function HomePage({ language }: PageProps) {
  const text = copy[language];

  return (
    <main>
      <section className="home-hero" aria-labelledby="home-hero-title">
        <video
          className="home-hero__media"
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.pexels.com/videos/4514359/pexels-photo-4514359.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=1080&w=1920"
        >
          <source
            src="https://videos.pexels.com/video-files/4514359/4514359-hd_1920_1080_24fps.mp4"
            type="video/mp4"
          />
        </video>
        <div className="home-hero__veil" />
        <div className="home-hero__content">
          <p className="home-hero__eyebrow">{text.hero.eyebrow}</p>
          <h1 id="home-hero-title">{text.hero.title}</h1>
          <a className="button button--light" href="#business">
            {text.hero.cta}
            <ArrowIcon />
          </a>
        </div>
        <a className="hero-scroll" href="#about">
          <span>{text.hero.scroll}</span>
          <ArrowIcon direction="down" />
        </a>
      </section>

      <section className="section about-section" id="about">
        <div className="section-shell">
          <Reveal className="about-heading">
            <Eyebrow>{text.about.label}</Eyebrow>
            <h2 className="display-heading preserve-lines">{text.about.title}</h2>
          </Reveal>
          <div className="about-layout">
            <Reveal className="about-copy" delay={100}>
              <p className="lead">{text.about.intro}</p>
              {text.about.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </Reveal>
            <div className="image-composition" aria-hidden="true">
              <Reveal className="image-composition__primary" delay={100}>
                <img
                  src="https://images.unsplash.com/photo-1607385404764-380a5e06c7fe?auto=format&fit=crop&w=1200&q=85"
                  alt=""
                />
              </Reveal>
              <Reveal className="image-composition__secondary" delay={180}>
                <img
                  src="https://images.unsplash.com/photo-1508385082359-f38ae991e8f2?auto=format&fit=crop&w=900&q=85"
                  alt=""
                />
              </Reveal>
              <Reveal className="image-composition__tertiary" delay={260}>
                <img
                  src="https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=900&q=85"
                  alt=""
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="section business-section" id="business">
        <div className="section-shell">
          <Reveal className="business-intro">
            <Eyebrow light>{text.business.label}</Eyebrow>
            <h2 className="display-heading display-heading--light">{text.business.title}</h2>
            <p>{text.business.intro}</p>
          </Reveal>
          <div className="service-list">
            {text.business.services.map((service, index) => (
              <Reveal
                className={`service-row ${index % 2 ? "service-row--reverse" : ""}`}
                key={service.number}
              >
                <div className="service-row__media">
                  <img src={service.image} alt="" loading="lazy" />
                </div>
                <div className="service-row__content">
                  <span className="service-number">{service.number}</span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  <ul className="clean-list">
                    {service.points.map((point) => (
                      <li key={point}>
                        <span aria-hidden="true">↗</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section why-section">
        <div className="why-visual" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1563164232-b6ee5dd3e821?auto=format&fit=crop&w=1600&q=85"
            alt=""
            loading="lazy"
          />
        </div>
        <div className="why-content">
          <Reveal>
            <Eyebrow>{text.why.label}</Eyebrow>
            <h2 className="display-heading">{text.why.title}</h2>
          </Reveal>
          <div className="why-points">
            {text.why.points.map(([title, description], index) => (
              <Reveal className="why-point" delay={index * 60} key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="purpose-section">
        <div className="purpose-section__backdrop" />
        <div className="section-shell purpose-section__inner">
          <Reveal>
            <Eyebrow light>{text.purpose.label}</Eyebrow>
          </Reveal>
          <div className="purpose-grid">
            {text.purpose.items.map((item, index) => (
              <Reveal className="purpose-item" delay={index * 100} key={item.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h2>{item.title}</h2>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section values-section">
        <div className="section-shell">
          <div className="values-intro">
            <Reveal>
              <Eyebrow>{text.values.label}</Eyebrow>
              <h2 className="values-word">{text.values.title}</h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="lead">{text.values.intro}</p>
            </Reveal>
          </div>
          <div className="values-grid">
            {text.values.items.map(([letter, title, description], index) => (
              <Reveal className="value-item" delay={index * 50} key={letter}>
                <span>{letter}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function PageHero({
  label,
  title,
  intro,
  image,
}: {
  label: string;
  title: string;
  intro: string;
  image: string;
}) {
  return (
    <section className="page-hero">
      <img className="page-hero__image" src={image} alt="" />
      <div className="page-hero__veil" />
      <div className="page-hero__content">
        <Eyebrow light>{label}</Eyebrow>
        <h1>{title}</h1>
        <p>{intro}</p>
      </div>
    </section>
  );
}

function TeamPage({ language }: PageProps) {
  const text = copy[language].team;
  const team = language === "en" ? teamEn : teamTh;

  return (
    <main>
      <PageHero
        label={text.label}
        title={text.title}
        intro={text.intro}
        image="https://images.unsplash.com/photo-1506869640319-fe1a24fd76dc?auto=format&fit=crop&w=2200&q=85"
      />
      <section className="section team-intro">
        <div className="section-shell team-intro__inner">
          <Reveal>
            <h2 className="display-heading">{text.introTitle}</h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="lead">{text.intro}</p>
          </Reveal>
        </div>
      </section>
      <section className="team-list">
        {team.map((leader, index) => (
          <article className={`leader ${index % 2 ? "leader--reverse" : ""}`} key={leader.name}>
            <Reveal className="leader__portrait">
              <img src={leader.image} alt={leader.name} loading="lazy" />
            </Reveal>
            <Reveal className="leader__profile" delay={100}>
              <p className="leader__role">{leader.role}</p>
              <h2>{leader.name}</h2>
              {leader.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </Reveal>
          </article>
        ))}
      </section>
    </main>
  );
}

function CareersPage({ language }: PageProps) {
  const text = copy[language].careers;

  return (
    <main>
      <PageHero
        label={text.label}
        title={text.title}
        intro={text.intro}
        image="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=2200&q=85"
      />

      <section className="section career-reasons">
        <div className="section-shell">
          <Reveal>
            <Eyebrow>{text.label}</Eyebrow>
            <h2 className="display-heading">{text.whyTitle}</h2>
          </Reveal>
          <div className="reason-grid">
            {text.reasons.map(([title, description], index) => (
              <Reveal className="reason" delay={index * 70} key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="career-culture">
        <div className="career-culture__image">
          <img
            src="https://images.unsplash.com/photo-1563433571545-99e130f273f2?auto=format&fit=crop&w=1400&q=85"
            alt=""
            loading="lazy"
          />
        </div>
        <Reveal className="career-culture__copy">
          <Eyebrow>{text.cultureTitle}</Eyebrow>
          <h2>{text.cultureTitle}</h2>
          <p className="lead">{text.culture}</p>
        </Reveal>
      </section>

      <section className="section openings-section">
        <div className="section-shell openings-layout">
          <Reveal className="openings-intro">
            <Eyebrow>{text.openingsTitle}</Eyebrow>
            <h2 className="display-heading">{text.openingsTitle}</h2>
            <p>{text.openingsIntro}</p>
          </Reveal>
          <div className="role-list">
            {text.roles.map((role, index) => (
              <Reveal className="role-row" delay={index * 40} key={role}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{role}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="career-cta">
        <div>
          <Reveal>
            <Eyebrow light>{text.label}</Eyebrow>
            <h2>{text.ctaTitle}</h2>
            <p>{text.cta}</p>
            <a className="button button--light" href={`mailto:${company.careersEmail}`}>
              {text.ctaButton}
              <ArrowIcon />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function ContactPage({ language }: PageProps) {
  const text = copy[language].contact;
  const address = language === "en" ? company.addressEn : company.addressTh;

  return (
    <main>
      <section className="contact-hero">
        <div className="contact-hero__intro">
          <Reveal>
            <Eyebrow>{text.label}</Eyebrow>
            <h1>{text.title}</h1>
            <p>{text.intro}</p>
          </Reveal>
        </div>
        <div className="contact-hero__image" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=85"
            alt=""
          />
        </div>
      </section>

      <section className="contact-details">
        <div className="contact-map">
          <iframe
            title="Power Asset Management location"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=Skyy%209%20Centre%20Bangkok&output=embed"
          />
        </div>
        <div className="contact-list">
          <Reveal className="contact-item">
            <p>{text.location}</p>
            <h2>{address}</h2>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Skyy+9+Centre+Bangkok"
              target="_blank"
              rel="noreferrer"
            >
              {text.directions}
              <ArrowIcon />
            </a>
          </Reveal>
          <Reveal className="contact-item" delay={80}>
            <p>{text.email}</p>
            <h2>
              <a href={`mailto:${company.email}`}>{company.email}</a>
            </h2>
          </Reveal>
          <Reveal className="contact-item" delay={120}>
            <p>{text.phone}</p>
            <h2>
              <a href="tel:+6621245242">{company.phone}</a>
            </h2>
          </Reveal>
          <Reveal className="contact-item" delay={160}>
            <p>{text.line}</p>
            <h2>
              <a href={company.lineUrl} target="_blank" rel="noreferrer">
                {company.line}
              </a>
            </h2>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function Footer({ language }: { language: Language }) {
  const text = copy[language].footer;
  const address = language === "en" ? company.addressEn : company.addressTh;

  return (
    <footer className="site-footer">
      <div className="site-footer__top">
        <div className="site-footer__brand">
          <img src="/assets/logo-white.jpg" alt="Power Asset Management" />
          <p>{text.statement}</p>
        </div>
        <div className="site-footer__column">
          <p className="footer-label">{text.contact}</p>
          <a href={`mailto:${company.email}`}>{company.email}</a>
          <a href="tel:+6621245242">{company.phone}</a>
          <a href={company.lineUrl} target="_blank" rel="noreferrer">
            LINE {company.line}
          </a>
        </div>
        <div className="site-footer__column">
          <p className="footer-label">{text.address}</p>
          <p>{address}</p>
        </div>
      </div>
      <div className="site-footer__bottom">
        <p>
          © {new Date().getFullYear()} Power Asset Management. {text.rights}
        </p>
        <div className="social-links">
          <a href={company.facebook} target="_blank" rel="noreferrer">
            Facebook
          </a>
          <a href={company.linkedin} target="_blank" rel="noreferrer">
            LinkedIn
          </a>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  const language = languageFromPath(pathname);
  const page = pageFromPath(pathname);

  useLayoutEffect(() => {
    document.documentElement.lang = language;
    const pageName =
      page === "home"
        ? language === "en"
          ? "About Us"
          : "เกี่ยวกับเรา"
        : copy[language].nav[page];
    document.title = `${pageName} | Power Asset Management`;
    window.scrollTo(0, 0);
  }, [language, page]);

  const pageComponent = {
    home: <HomePage language={language} />,
    team: <TeamPage language={language} />,
    careers: <CareersPage language={language} />,
    contact: <ContactPage language={language} />,
  }[page];

  return (
    <div className={language === "th" ? "app app--thai" : "app"}>
      <Header language={language} page={page} />
      {pageComponent}
      <Footer language={language} />
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
